import { create } from 'zustand';
import {
  CreateObjectiveRequest,
  Objective,
  UpdateObjectiveRequest,
} from '../types/objective.type';
import {
  getObjectives,
  getObjective,
  createObjective,
  updateObjective,
  deleteObjective,
  linkObjectiveTask,
  unlinkObjectiveTask,
  saveObjectiveTaskLinks,
} from '../services/objective.service';
import { LinkObjectiveTaskRequest, ObjectiveTaskLink } from '../types/objective.type';

interface ObjectiveState {
  objectives: Objective[];
  currentObjective: Objective | null;
  isLoading: boolean;
  isDetailLoading: boolean;
  error: string | null;
  fetchObjectives: () => Promise<void>;
  fetchObjective: (id: string) => Promise<Objective>;
  addObjective: (data: CreateObjectiveRequest) => Promise<Objective>;
  editObjective: (id: string, data: UpdateObjectiveRequest) => Promise<Objective>;
  removeObjective: (id: string) => Promise<void>;
  linkTask: (objectiveId: string, body: LinkObjectiveTaskRequest) => Promise<void>;
  unlinkTask: (objectiveId: string, ownerId: string, taskListId: string, googleTaskId: string) => Promise<void>;
  saveTaskLinks: (objectiveId: string, links: ObjectiveTaskLink[]) => Promise<void>;
  clearCurrentObjective: () => void;
}

export const useObjectiveStore = create<ObjectiveState>((set, get) => ({
  objectives: [],
  currentObjective: null,
  isLoading: false,
  isDetailLoading: false,
  error: null,

  fetchObjectives: async () => {
    set({ isLoading: true, error: null });
    try {
      const res = await getObjectives();
      set({ objectives: res.data ?? [], isLoading: false });
    } catch (e: any) {
      set({ error: e.message || 'Failed to load objectives', isLoading: false });
    }
  },

  fetchObjective: async (id: string) => {
    set({ isDetailLoading: true, error: null });
    try {
      const objective = await getObjective(id);
      set({ currentObjective: objective, isDetailLoading: false });
      set(state => ({
        objectives: state.objectives.some(o => o.id === id)
          ? state.objectives.map(o => (o.id === id ? { ...objective, tasks: undefined } : o))
          : state.objectives,
      }));
      return objective;
    } catch (e: any) {
      set({ error: e.message || 'Failed to load objective', isDetailLoading: false });
      throw e;
    }
  },

  addObjective: async (data) => {
    const objective = await createObjective(data);
    set(s => ({ objectives: [objective, ...s.objectives] }));
    return objective;
  },

  editObjective: async (id, data) => {
    const updated = await updateObjective(id, data);
    set(s => ({
      objectives: s.objectives.map(o => (o.id === id ? { ...updated, tasks: o.tasks } : o)),
      currentObjective: s.currentObjective?.id === id ? { ...updated, tasks: s.currentObjective.tasks } : s.currentObjective,
    }));
    return updated;
  },

  removeObjective: async (id) => {
    await deleteObjective(id);
    set(s => ({
      objectives: s.objectives.filter(o => o.id !== id),
      currentObjective: s.currentObjective?.id === id ? null : s.currentObjective,
    }));
  },

  linkTask: async (objectiveId, body) => {
    await linkObjectiveTask(objectiveId, body);
    await get().fetchObjective(objectiveId);
  },

  unlinkTask: async (objectiveId, ownerId, taskListId, googleTaskId) => {
    await unlinkObjectiveTask(objectiveId, ownerId, taskListId, googleTaskId);
    await get().fetchObjective(objectiveId);
  },

  saveTaskLinks: async (objectiveId, links) => {
    await saveObjectiveTaskLinks(objectiveId, links);
    await get().fetchObjective(objectiveId);
  },

  clearCurrentObjective: () => set({ currentObjective: null }),
}));
